import { faBell, faBellSlash, IconDefinition } from "@elynx/free-solid-svg-icons";
import { faCalendarTimes } from "@elynx/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Card, CardContent, CardHeader, Link, Typography, useTheme } from "@mui/material";
import React, { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AlarmNotificationEndingReasonEnum } from "../../common/enums/AlarmNotificationEndingReasonEnum";
import ActionMenu from "../action-menu/ActionMenu";
import { ActionMenuItem } from "../action-menu/ActionMenuItem";
import { AlarmSetpoint } from "./AlarmSetpointList";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { getTagRoute } from "../../common/utilities/RouteHelpers";
import { UIViewEnum } from "../../common/enums/UIViewEnum";
import { format } from "date-fns";
import {isMobile, isTablet} from 'react-device-detect';
import { AlarmState, getAlarmState } from "../../queries/alarm-setpoints/Common";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { ObjectFeaturesEnum } from "../../common/enums/ObjectFeaturesEnum";
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { useObjectViewState } from "../../queries/object-view-state/UseObjectViewState";
import useNavigateTo, { NavigateToOptions } from "../../hooks/use-navigate-to/useNavigateTo";
import { NewViewTypeEnum } from "../../common/enums/NewViewTypeEnum";
import { SetPointTypeEnum } from "../../common/enums/SetpointTypeEnum";

export type AlarmSetpointListItemProps = {
    item: AlarmSetpoint,
    onAcknowledge?: (setpoint: AlarmSetpoint) => void;
    onDisable?: (setpoint: AlarmSetpoint) => void;
    onEdit?: (setpoint: AlarmSetpoint) => void;
    onUnlinkTag?: (setpoint: AlarmSetpoint) => void;
    onTest?: (setpoint: AlarmSetpoint) => void;
    onRename?: (setpoint: AlarmSetpoint) => void;
};

export function AlarmSetpointListItem({
    item,
    onAcknowledge,
    onDisable,
    onEdit,
    onUnlinkTag,
    onTest,
    onRename

}: AlarmSetpointListItemProps) {
    const theme = useTheme();
    //const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));
    const { t } = useTranslation();
    const routeContext = useRouteContext();
    const [userState] = useUserContext();
    const [offlineState] = useOfflineDetectContext();
    const navigateTo = useNavigateTo();
    const isTagView = routeContext.contextObject?.objectType === eLynxObjectTypeEnum.Tag;

    const isPhone = isMobile && !isTablet;
    const alarmState = getAlarmState(item);

    const { data: objectViewState } = useQueryNotifications(useObjectViewState)({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number,
        contextObject: routeContext.contextObject,
        offline: offlineState.offline
    });


    const hasAlarmModify = ((objectViewState?.features ?? 0) & ObjectFeaturesEnum.AlarmModify) > 0;


    function onNavigate(item: AlarmSetpoint, shiftKey: boolean, ctrlKey: boolean) {

        let options: NavigateToOptions | undefined;

        if (shiftKey || ctrlKey) {
            options = {
                newViewType: shiftKey ? NewViewTypeEnum.NewWindow : NewViewTypeEnum.NewTab
            }
        }


        const url = getTagRoute(
            UIViewEnum.Tag_Details,
            routeContext.groupID,
            routeContext.wellID,
            routeContext.deviceID ?? (item.tagObjectTypeID === eLynxObjectTypeEnum.Device
                ? item.tagObjectId
                : undefined),
            routeContext.tankBatteryID,
            routeContext.tankID ?? (item.tagObjectTypeID === eLynxObjectTypeEnum.Tank
                ? item.tagObjectId
                : undefined),
            item.tagID
        );

        navigateTo(url, options);
    };

    function getTitle(): ReactNode {
        return (
            <Box>
                {
                    !isTagView &&
                    <Link
                        sx={{
                            color: "text.primary",
                            "&:hover": {
                                cursor: "pointer"
                            }
                        }}
                        onClick={(ev) => onNavigate(item, ev.shiftKey, ev.ctrlKey)}
                    >
                        {item.tagName}
                    </Link>
                }
                {
                    isTagView && 
                        item.setPointName
                }
                {
                    (!isTagView &&
                        (routeContext.contextObject?.id !== item.tagObjectId ||
                            routeContext.contextObject.objectType !== item.tagObjectTypeID)) &&
                    ` (${item.tagObjectName})`
                }
            </Box>
        );
    }

    function getSubheader(): ReactNode {
        return !isTagView ? item.setPointName : undefined;
    }

    function getAvatar() {
        return (
            <Box sx={{
                position: "relative",
                width: (theme) => theme.spacing(4)
            }}
            >
                <FontAwesomeIcon icon={getIcon()} size="2x" color={getIconColor()} />
                {
                    item.hasDisableSchedule && !item.disabled &&
                    <FontAwesomeIcon
                        icon={faCalendarTimes}
                        size="1x"
                        style={{
                            position: "absolute",
                            top: 0,
                            right: theme.spacing(-0.9),
                            color: theme.palette.grey[600]
                        }} />
                }
            </Box>
        );
    }

    function getIcon(): IconDefinition {
        return item.disabled ? faBellSlash : faBell;
    }

    function getIconColor(): string | undefined {
        return alarmState===AlarmState.Disabled
            ? theme.palette.grey[600]
            : alarmState===AlarmState.Acknowledged
                ? item.acknowledgeColor
                : alarmState===AlarmState.InAlarm
                    ? item.alarmColor
                    : undefined;
    }

    function getCurrentStateText(): string {
        if(item.setPointType === SetPointTypeEnum.TimeComparison) {
            return alarmState===AlarmState.Acknowledged
                ? t("alarmSetpointList.timeAcknowledgedStateText", {
                    currentTimeText: item.currentValueTimestamp 
                        ? format(new Date(item.currentValueTimestamp), "P pp")
                        : "Acknowledged"
                })
                : alarmState===AlarmState.InAlarm
                    ? t("alarmSetpointList.timeAlarmStateText", {
                        currentTimeText: item.currentValueTimestamp
                            ? format(new Date(item.currentValueTimestamp), "P pp") 
                            : "Alarm"
                    })
                    : t("alarmSetpointList.timeNormalStateText", {
                    currentTimeText: item.currentValueTimestamp
                        ? format(new Date(item.currentValueTimestamp), "P pp")
                        : "Normal",
                });
        } else {
            return alarmState===AlarmState.Acknowledged
                ? t("alarmSetpointList.valueAcknowledgedStateText", item)
                : alarmState===AlarmState.InAlarm
                    ? t("alarmSetpointList.valueAlarmStateText", item)
                    : t("alarmSetpointList.valueNormalStateText", item);
        }
    }

    function getAcknowledgementText(): string {
        const timestamp = item.alarmAcknowledgeDateTime
            ? format(new Date(item.alarmAcknowledgeDateTime), "P pp")
            : "";

        const source = item.alarmEndingReason
            ? t(`enums.alarmNotificationEndingReason.${AlarmNotificationEndingReasonEnum[item.alarmEndingReason].toLowerCase()}`)
            : "";

        return t("alarmSetpointList.acknowledgementText", {
            source: source,
            alarmAcknowledgedBy: item.alarmAcknowledgedBy,
            timestamp: timestamp
        });
    }

    function getLastAlarmText(): string {
        const timestamp = item.lastAlarmTimestamp
            ? format(new Date(item.lastAlarmTimestamp), "P pp")
            : "";

        return t("alarmSetpointList.lastAlarmText", { lastAlarmTimestamp: timestamp });
    }

    function getValueText(): string {
        const timestamp = item.currentValueTimestamp
            ? format(new Date(item.currentValueTimestamp), "P pp")
            : "";

        return t("alarmSetpointList.valueText", {
            currentValueText: item.currentValueText,
            timestamp: timestamp
        });
    }

    function getDisabledText(): string {
        return t("alarmSetpointList.disabledUntilText", {
            timestamp: format(new Date(item.disabledUntil), "P pp")
        });
    }

    function acknowledgeOnClick(): void {
        if (onAcknowledge) {
            onAcknowledge(item);
        }
    }

    function disableOnClick(): void {
        if (onDisable) {
            onDisable(item);
        }
    }

    function editOnClick(): void {
        if (onEdit) {
            onEdit(item);
        }
    }

    function renameOnClick(): void {
        if (onRename) {
            onRename(item);
        }
    }


    function unlinkOnClick(): void {
        if (onUnlinkTag) {
            onUnlinkTag(item);
        }
    }
    function testOnClick(): void {
        if (onTest) {
            onTest(item);
        }
    }

    const baseActionItems = new Array<ActionMenuItem>(
        {
            label: t("alarmSetpointList.itemMenu.acknowledgeSetpoint"),
            onClick: acknowledgeOnClick,
            disabled: !item.inAlarm || item.alarmAcknowledged
        }, {
            label: item.disabled
                ? t("alarmSetpointList.itemMenu.enableSetpoint")
                : item.hasDisableSchedule
                    ? t("alarmSetpointList.itemMenu.editDisableSchedule")
                    : t("alarmSetpointList.itemMenu.disableSetpoint"),
            onClick: disableOnClick,
            disabled: item.inAlarm && !item.alarmAcknowledged
        }, {
            label: t("alarmSetpointList.itemMenu.edit"),
            onClick: editOnClick,
        }, {
            label: t("alarmSetpointList.itemMenu.rename"),
            onClick: renameOnClick,
        }, {
            label: t("alarmSetpointList.itemMenu.unlink"),
            onClick: unlinkOnClick,
        }, {
            label: t("alarmSetpointList.itemMenu.test"),
            onClick: testOnClick,
        }
    );

    const actionItems = hasAlarmModify ? baseActionItems : baseActionItems.slice(0, 2);

    return (
        <Card variant="outlined">
            <CardHeader
                sx={{
                    padding: 1
                }}
                title={getTitle()}
                avatar={getAvatar()}
                subheader={getSubheader()}
                action={<ActionMenu actionItems={actionItems} />}
            >
            </CardHeader>

            <CardContent sx={{
                padding: (theme) => `0 ${theme.spacing(1)} ${theme.spacing(1)} ${theme.spacing(1)} !important`,
            }}
            >
                <Typography variant="body2">
                    <span dangerouslySetInnerHTML={{ __html: item.setPointDescription }}></span>
                </Typography>
{/* Need to check here for setpoint type */}
                {!isPhone && item.currentValueText &&
                    <Typography variant="body2">
                        {t("alarmSetpointList.currentState")} <span style={{ color: getIconColor(), fontWeight: "bold" }}>{getCurrentStateText()}</span>
                    </Typography>
                }

                {isPhone && item.currentValueText &&
                    <Typography variant="body2" style={{ fontWeight: "bold" }}>
                        {getValueText()}
                    </Typography>
                }

                {alarmState === AlarmState.Acknowledged &&
                    <Typography variant="body2" style={{ color: "green", fontStyle: "italic" }}>
                        {getAcknowledgementText()}
                    </Typography>
                }

                {isPhone && alarmState !== AlarmState.Acknowledged && item.lastAlarmTimestamp &&
                    <Typography variant="body2" style={{ fontStyle: "italic" }}>
                        {getLastAlarmText()}
                    </Typography>
                }

                {item.disabled &&
                    <Typography variant="body2" style={{ color: "green", fontStyle: "italic" }}>
                        {getDisabledText()}
                    </Typography>
                }
            </CardContent>
        </Card>
    );
}